import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams, useHistory } from "react-router";
import { ReactSVG } from "react-svg";
import { CheckCircleOutlined } from "@ant-design/icons";
import { Button, Checkbox, Collapse, Form, Modal, notification, Table } from "antd";
import moment from "moment";
import _find from "lodash/find";
import _isEmpty from "lodash/isEmpty";
import _sumBy from "lodash/sumBy";

import { selectLocation } from "modules/admin/orders/store/LocationSlice";
import { getLocation } from "modules/admin/orders/store/LocationThunks";
import { selectAllPackageTypes } from "modules/client/package-types/store/slice";
import { getPackageTypeList } from "modules/client/package-types/store/thunks";
import { withPermission } from "hocs/withPermission";
import { UserRole, LOCATION_TYPES, TIME_FORMAT_BACKEND } from "configs/constants";

import OrdersHeader from "../components/header/OrdersHeader";
import AddressFormGroup from "../components/form/AddressFormGroup";
import PackageList from "../components/PackageList";
import PackageListDrop from "../components/PackageListDrop";
import PackForm from "../components/form/PackForm";
import SelectPack from "../components/form/SelectPack";
import orderApi from "../store/api";

import "./CreateOrderPage.scss";
import { convertEmergencyStatus } from "helper/util";
import PoolStatus from "components/items/PoolStatus";
import paginationFunc from "../../../../helper/hooks/pagination";
import { selectMetadata } from "../store/orderSlice";
import { updatePaginationPackages } from "modules/client/pool/store/slice";
import { refreshPackagesV2 } from "helper/getPackagesV2";
import FilterFlexioPool from "modules/admin/pool/components/Filter";
import { ArrowRightIcon } from "components/svgs";

const { Panel } = Collapse;

const pickupInit = {
  fromTime: moment("00:00", "HH:mm"),
  toTime: moment("23:59", "HH:mm"),
  duration: 10,
  phoneNumbers: [{ type: "contact", phone: "" }]
};

const dropoffInit = {
  fromTime: moment("00:00", "HH:mm"),
  toTime: moment("23:59", "HH:mm"),
  duration: 10,
  phoneNumbers: [{ type: "contact", phone: "" }]
};

const AddJobPage = ({ isAdmin }) => {
  const history = useHistory();
  const params = useParams();
  const dispatch = useDispatch();
  const routeId = params.id;
  const routesUri = isAdmin ? "/admin/routes" : "/routes";
  const breadCrumbs = [
    { text: "ACTIVE ROUTES", url: routesUri },
    { text: routeId?.substr(0, 8).toUpperCase(), url: `${routesUri}/${params.id}` },
    { text: "ADD STOPS" }
  ];
  const pickupLocation = useSelector(selectLocation);
  const packageTypeList = useSelector(selectAllPackageTypes);
  const [isSubmiting, setIsSubmiting] = useState(false);
  const [form] = Form.useForm();
  const [activeKeyPickup, setActiveKeyPickup] = useState([LOCATION_TYPES.PICKUP]);
  const [activeKeyDropoff, setActiveKeyDropoff] = useState([LOCATION_TYPES.DROPOFF]);
  const [addressInfo, setAddressInfo] = useState({ pickup: {}, dropoff: {} });
  const [imagesAddressInfo, setImagesAddressInfo] = useState({ pickup: [], dropoff: [] });
  const [visible, setVisible] = useState(false);
  const [formPack] = Form.useForm();
  const [formSelectPack] = Form.useForm();
  const [packages, setPackages] = useState([]);
  const [selectedPack, setSelectedPack] = useState(null);
  const [selectedInfo, setSelectedInfo] = useState({ id: null, typePack: null });
  const [isAddEditPool, setIsAddEditPool] = useState(false);
  const [poolPackages, setPoolPackages] = useState([]);
  const [filterValues, setFilterValues] = useState({});
  const [selectAll, setSelectAll] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const metadata = useSelector(selectMetadata);
  const [rowSelectedPoolPackage, setRowSelectedPoolPackage] = useState([]);
  const [poolSelectedRowKeys, setPoolSelectedRowKeys] = useState([]);
  const [poolSelectedPackagesToShow, setPoolSelectedPackagesToShow] = useState([]);
  const [pickupLocationId, setPickupLocationId] = useState(null);
  const pagination = useMemo(
    () =>
      paginationFunc(
        { ...metadata, itemsPerPage: metadata?.itemsPerPage || 20 },
        "Packages",
        updatePaginationPackages,
        dispatch
      ),
    [metadata, dispatch]
  );

  const page = useMemo(() => metadata?.currentPage || 1, [metadata?.currentPage]);
  const pageSize = useMemo(() => metadata?.itemsPerPage || 20, [metadata?.itemsPerPage]);

  const handleSelectAll = () => {
    setSelectAll(!selectAll);
    const selectedPackages = selectAll ? [] : poolPackages?.map((pack) => pack.id);
    setRowSelectedPoolPackage(selectedPackages);
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      render: (value) => {
        return (
          <p style={{ margin: 0, color: "#1f3998" }}>
            {value.substr(0, 8).toUpperCase()}
          </p>
        );
      },
      width: 120
    },
    {
      title: "Pickup Stop Name",
      dataIndex: "pickupStopName",
      width: 120
    },
    {
      title: "Pickup Address",
      dataIndex: "pickupAddress",
      width: 180
    },
    {
      title: "Pickup Contact Name",
      dataIndex: "pickupContactName",
      width: 120
    },
    {
      title: "Pickup Date",
      dataIndex: "pickupDate",
      render: (value) => <>{moment(value).format("LLL")}</>,
      width: 120
    },
    {
      title: "Dropoff Stop Name",
      dataIndex: "dropoffStopName",
      width: 120
    },
    {
      title: "Dropoff Address",
      dataIndex: "dropoffAddress",
      width: 200
    },
    {
      title: "Dropoff Contact Name",
      dataIndex: "dropoffContactName",
      width: 120
    },
    {
      title: "status",
      dataIndex: "status",
      render: (_, record) => (
        <PoolStatus
          value={convertEmergencyStatus(record)}
          record={record}
          setSelectedPackage={setRowSelectedPoolPackage}
          isAdmin={true}
        />
      ),
      width: 120
    }
  ];

  // Fetch saved location data from backend when location ID is provided in URL params
  useEffect(() => {
    if (_isEmpty(params.eid)) return;
    dispatch(getLocation(params.eid));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.eid]);

  // When location is loaded, populate form and state with location data and ID for warehouse filtering
  useEffect(() => {
    if (_isEmpty(pickupLocation)) return;
    setPickupLocationId(pickupLocation.id);
    if (pickupLocation.attachments) {
      const attachments = pickupLocation.attachments.map((file) => {
        return { ...file, uid: file.id, status: "done" };
      });
      setImagesAddressInfo({ ...imagesAddressInfo, pickup: attachments });
    }
    setAddressInfo({
      pickup: {
        address: pickupLocation.address,
        lngLat: [pickupLocation.lng, pickupLocation.lat]
      }
    });
    const { start, end } = pickupLocation;
    const fromTime = start ? moment(start, TIME_FORMAT_BACKEND) : null;
    const toTime = end ? moment(end, TIME_FORMAT_BACKEND) : null;
    form.setFieldsValue({
      pickup: {
        ...pickupLocation,
        fromTime,
        toTime
      },
      dropoff: { fromTime, toTime }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickupLocation]);

  useEffect(() => {
    dispatch(getPackageTypeList());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getPoolPackages = useCallback(() => {
    refreshPackagesV2(
      filterValues,
      page,
      pageSize,
      setPoolPackages,
      updatePaginationPackages,
      dispatch
    );
  }, [filterValues, page, pageSize, dispatch]);

  useEffect(() => {
    getPoolPackages();
  }, [getPoolPackages]);

  // Help with selecting pool packages on list
  const onSelectChange = (keys) => {
    setPoolSelectedRowKeys(keys);
    setPoolSelectedPackagesToShow(
      poolPackages.filter((poolPackItem) => keys.includes(poolPackItem.id))
    );
  };
  const rowSelection = {
    selectedRowKeys: poolSelectedRowKeys,
    onChange: onSelectChange
  };

  const handleFilterChange = (newFilterValues) => {
    console.log(newFilterValues);
    setFilterValues(newFilterValues);
  };

  const handleClickFilter = () => {
    getPoolPackages();
  };

  const handleReset = () => {
    setFilterValues({});
  };

  const expandIconDynamic = (props) => {
    if (props.isActive) {
      return <ReactSVG src="/icons/collapse.svg" />;
    } else {
      return <ReactSVG src="/icons/dropoff.svg" />;
    }
  };

  const handleChangePickupAddress = (value) => {
    const pickupValues = { ...form.getFieldValue(LOCATION_TYPES.PICKUP) };
    pickupValues.address = value.address;
    form.setFieldsValue({
      pickup: pickupValues
    });
    setAddressInfo({ ...addressInfo, pickup: value });
    if (value?.id) {
      setPickupLocationId(value.id);
    }
  };

  const handleChangeDropoffAddress = (value) => {
    const dropoffValues = { ...form.getFieldValue(LOCATION_TYPES.DROPOFF) };
    dropoffValues.address = value.address;
    form.setFieldsValue({
      dropoff: dropoffValues
    });
    setAddressInfo({ ...addressInfo, dropoff: value });
  };

  const handleChangeTime = (value, groupName, fieldName) => {
    const stopValues = { ...form.getFieldValue(groupName) };
    stopValues[fieldName] = value;
    const stopObj = {};
    stopObj[groupName] = stopValues;
    form.setFieldsValue(stopObj);
  };

  const handleCancel = () => {
    form.resetFields();
    history.goBack();
  };

  const openPackCreateUpdate = (pack = null) => {
    setVisible(true);
  };

  const handleSavePack = (values) => {
    const newPackages = [...packages];
    const pickupId = pickupLocationId || params.eid || pickupLocation?.id || "pickup";
    if (!values.id) {
      newPackages.push({
        ...values,
        id: `${new Date().getTime()}-pack`,
        pickupId: pickupId
      });
    } else {
      const indexpack = newPackages.findIndex((pack) => pack.id === values.id);
      delete values.id;
      const newPack = {
        ...newPackages[indexpack],
        ...values,
        pickupId: pickupId
      };
      newPackages.splice(indexpack, 1, newPack);
    }
    setPackages(newPackages);
    handleCancelModal();
  };

  const removePack = (values) => {
    let newPackages = [];

    if (!values?.orderV2) {
      newPackages = [...packages];
      const indexpack = newPackages.findIndex((pack) => pack.id === values.id);
      newPackages.splice(indexpack, 1);
      setPackages(newPackages);
    } else {
      newPackages = [...poolSelectedPackagesToShow];
      const indexpack = newPackages.findIndex((pack) => pack.id === values.id);
      newPackages.splice(indexpack, 1);
      setPoolSelectedPackagesToShow(newPackages);
      setPoolSelectedRowKeys(
        newPackages.map((poolSelectedRowKey) => poolSelectedRowKey?.id)
      );
    }
  };

  const handleCancelModal = () => {
    formPack.resetFields();
    setSelectedPack(null);
    setVisible(false);
    setSelectedInfo({ id: null, typePack: null });
  };

  // Open modal to select packages for dropoff
  const openSelectedPack = () => {
    setSelectedInfo({
      id: "dropoff",
      typePack: "dropoff"
    });
    setVisible(true);
  };

  // Get packages that still have available quantity for selection
  const getPacksFromSelect = () => {
    return packages.filter((pack) => {
      if (pack.dropoffs && pack.dropoffs.length) {
        const quantityCurrent = _sumBy(
          pack.dropoffs.filter((drop) => drop.id !== "dropoff"),
          "quantity"
        );
        return !(pack.quantity - quantityCurrent === 0);
      }
      return true;
    });
  };

  // Handle package selection from SelectPack modal
  const handleSelectPack = (values) => {
    const newPackages = [...packages];
    const packsData = values.packs;
    const packIds = values.packs.map((p) => p.id);

    packages.forEach((packa) => {
      if (!packIds.includes(packa.id)) return;
      const indexPack = newPackages.findIndex((newP) => packa.id === newP.id);
      const packSend = packsData.find((packD) => packa.id === packD.id);
      const packDropoffs = newPackages[indexPack]?.dropoffs
        ? [...newPackages[indexPack].dropoffs]
        : [];
      const indexDrop = packDropoffs.findIndex((drop) => "dropoff" === drop.id);

      if (packSend.value === 0 && indexDrop === -1) return;
      if (packSend.value === 0 && indexDrop !== -1) {
        packDropoffs.splice(indexDrop, 1);
      } else if (packDropoffs.length === 0) {
        packDropoffs.push({
          id: "dropoff",
          quantity: packSend.value,
          barcode: packSend.barcode || ""
        });
      } else if (indexDrop === -1) {
        packDropoffs.push({
          id: "dropoff",
          quantity: packSend.value,
          barcode: packSend.barcode || ""
        });
      } else {
        packDropoffs.splice(indexDrop, 1, {
          id: "dropoff",
          quantity: packSend.value,
          barcode: packSend.barcode || ""
        });
      }

      newPackages.splice(indexPack, 1, {
        ...newPackages[indexPack],
        dropoffs: packDropoffs
      });
    });

    setPackages(newPackages);
    handleCancelModal();
  };

  const handleSubmit = async () => {
    try {
      let payloadPackages = [...packages];
      await form.validateFields();
      if (!payloadPackages?.length) {
        notification.error({
          message: "Please select at least 1 package",
          placement: "topRight"
        });
        return;
      }

      // The way to add packagesV2 to packages request object
      if (poolSelectedPackagesToShow.length) {
        payloadPackages = payloadPackages.map((pack) => ({
          ...pack,
          packageTypeId: null,
          packagesV2: poolSelectedPackagesToShow
        }));
      }
      const { pickup: pickupValues, dropoff: dropoffValues } = form.getFieldsValue();
      const { fromTime: fromPickupTime, toTime: toPickupTime } = pickupValues;
      const { fromTime: fromDropoffTime, toTime: toDropoffTime } = dropoffValues;
      const payload = {
        pickup: {
          ...pickupValues,
          lng: addressInfo[LOCATION_TYPES.PICKUP]?.lngLat[0],
          lat: addressInfo[LOCATION_TYPES.PICKUP]?.lngLat[1],
          start: fromPickupTime
            ? typeof fromPickupTime === "object"
              ? fromPickupTime.format(TIME_FORMAT_BACKEND)
              : moment(fromPickupTime).format(TIME_FORMAT_BACKEND)
            : undefined,
          end: toPickupTime
            ? typeof toPickupTime === "object"
              ? toPickupTime.format(TIME_FORMAT_BACKEND)
              : moment(toPickupTime).format(TIME_FORMAT_BACKEND)
            : undefined,
          files: imagesAddressInfo[LOCATION_TYPES.PICKUP]?.map((image) => image.id)
        },
        dropoff: {
          ...dropoffValues,
          lng: addressInfo[LOCATION_TYPES.DROPOFF].lngLat[0],
          lat: addressInfo[LOCATION_TYPES.DROPOFF]?.lngLat[1],
          start: fromDropoffTime
            ? typeof fromDropoffTime === "object"
              ? fromDropoffTime.format(TIME_FORMAT_BACKEND)
              : moment(fromDropoffTime).format(TIME_FORMAT_BACKEND)
            : undefined,
          end: toDropoffTime
            ? typeof toDropoffTime === "object"
              ? toDropoffTime.format(TIME_FORMAT_BACKEND)
              : moment(toDropoffTime).format(TIME_FORMAT_BACKEND)
            : undefined,
          files: imagesAddressInfo[LOCATION_TYPES.DROPOFF]?.map((image) => image.id)
        },
        routeId,
        packages: payloadPackages
      };
      setIsSubmiting(true);
      const addedJob = await orderApi.addJobToRoute(payload);
      if (addedJob) {
        // Redirect to dispatch page after successfully adding stop
        history.push(`${routesUri}/${routeId}/dispatch`);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setIsSubmiting(false);
    }
  };

  return (
    <div className="CreateOrderPage">
      {!isAdmin && <OrdersHeader breadCrumbs={breadCrumbs} hasButton={false} />}
      <div className="wrapper">
        <h2 className="title">Add Stops</h2>
        <div className="contentForm ml-0">
          <Form
            scrollToFirstError={true}
            layout="vertical"
            form={form}
            hideRequiredMark
            colon={false}
            initialValues={{ pickup: pickupInit, dropoff: dropoffInit }}
          >
            <div className="pickups block">
              <Collapse
                activeKey={activeKeyPickup}
                onChange={(key) => setActiveKeyPickup(key)}
                ghost
                expandIconPosition="end"
                expandIcon={(props) => expandIconDynamic(props)}
              >
                {/* PICKUP */}
                <Panel header="Pickup Location" key={LOCATION_TYPES.PICKUP}>
                  <AddressFormGroup
                    onSetAddress={handleChangePickupAddress}
                    address={addressInfo.pickup}
                    groupName={LOCATION_TYPES.PICKUP}
                    onChangeTime={handleChangeTime}
                    imagesAddressInfo={imagesAddressInfo}
                    onSetImagesAddressInfo={setImagesAddressInfo}
                  />
                  {poolSelectedRowKeys.length ? (
                    <>
                      <div style={{ display: "flex", justifyContent: "flex-end" }}>
                        <Button
                          type="link"
                          className="edit"
                          onClick={() => openPackCreateUpdate({}, true)}
                        >
                          Edit Pool Packages List
                        </Button>
                      </div>
                      {poolSelectedPackagesToShow.map((poolPack, index) => {
                        return (
                          <div key={index + 1} className="">
                            <b style={{ padding: "5px" }}>{index + 1}. Pool Package</b>{" "}
                            <span>#{poolPack.id.substr(0, 8).toUpperCase()} </span>
                            <div
                              className="ml-3 mb-3"
                              style={{ display: "flex", alignItems: "center" }}
                            >
                              {poolPack.pickupContactName}
                              <ArrowRightIcon /> {poolPack.dropoffContactName}
                              <Button
                                type="link"
                                className="delete"
                                onClick={() => removePack(poolPack)}
                              >
                                Delete
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </>
                  ) : (
                    <PackageList
                      packages={packages}
                      editPack={(pack) => openPackCreateUpdate(pack)}
                      addPack={() => openPackCreateUpdate()}
                      removePack={removePack}
                    />
                  )}
                </Panel>
              </Collapse>
            </div>
            {/* DROPOFF */}
            <div className="dropoffs block">
              <Collapse
                activeKey={activeKeyDropoff}
                onChange={(key) => setActiveKeyDropoff(key)}
                ghost
                expandIconPosition="end"
                expandIcon={(props) => expandIconDynamic(props)}
              >
                <Panel header="Dropoff Location" key={LOCATION_TYPES.DROPOFF}>
                  <AddressFormGroup
                    onSetAddress={handleChangeDropoffAddress}
                    address={addressInfo.dropoff}
                    groupName={LOCATION_TYPES.DROPOFF}
                    onChangeTime={handleChangeTime}
                    imagesAddressInfo={imagesAddressInfo}
                    onSetImagesAddressInfo={setImagesAddressInfo}
                  />
                  {!poolSelectedPackagesToShow.length ? (
                    <PackageListDrop
                      packages={packages}
                      id="dropoff"
                      selectedPack={openSelectedPack}
                      isAll={true}
                    />
                  ) : (
                    <div className="p-4">
                      <h1>PACKAGE REVIEW</h1>
                      {poolSelectedPackagesToShow.map((poolPack, index) => {
                        return (
                          <div key={index + 1} className="p-1">
                            <b style={{ padding: "5px" }}>{index + 1}. Pool Package</b>{" "}
                            <span>#{poolPack.id.substr(0, 8).toUpperCase()} </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </Panel>
              </Collapse>
            </div>
          </Form>
        </div>
      </div>
      <div className="footer">
        <Button size="large" onClick={handleCancel}>
          Cancel
        </Button>
        <Button
          type="primary"
          size="large"
          className="create-order"
          loading={isSubmiting}
          onClick={handleSubmit}
        >
          Add
          <CheckCircleOutlined />
        </Button>
      </div>
      <Modal
        title={
          !selectedPack && selectedInfo.typePack !== "dropoff" ? (
            <div>Add Package</div>
          ) : selectedPack && selectedInfo.typePack !== "dropoff" ? (
            <div>Edit Package</div>
          ) : (
            <div>Select Package</div>
          )
        }
        open={visible}
        onCancel={handleCancelModal}
        className="choose-package-modal"
        footer={
          <div className="buttons-modal">
            <Form.Item>
              <Button size="large" onClick={handleCancelModal}>
                Cancel
              </Button>
              <Button
                size="large"
                type="primary"
                onClick={() => {
                  if (selectedInfo.typePack === "dropoff") {
                    formSelectPack.submit();
                  } else {
                    formPack.submit();
                  }
                }}
              >
                Confirm
                <CheckCircleOutlined />
              </Button>
            </Form.Item>
          </div>
        }
        width={1024}
      >
        {selectedInfo.typePack !== "dropoff" && (
          <>
            <div
              className="pr-4 pl-4 pb-3"
              style={{ display: "flex", justifyContent: "space-between" }}
            >
              <Checkbox
                onChange={() => {
                  setIsAddEditPool(!isAddEditPool);
                  setRowSelectedPoolPackage([]);
                  setPoolSelectedPackagesToShow([]);
                  setPoolSelectedRowKeys([]);
                }}
                checked={isAddEditPool}
              >
                Pool
              </Checkbox>
              {isAddEditPool && !showFilter && (
                <Button onClick={() => setShowFilter(true)}>Show Filter</Button>
              )}
            </div>
            {!isAddEditPool ? (
              <div className="pl-5 pr-5">
                <PackForm
                  pack={selectedPack}
                  formPack={formPack}
                  onOk={handleSavePack}
                  packageTypeList={packageTypeList}
                  poolPackages={rowSelectedPoolPackage}
                  pickupLocationId={pickupLocationId}
                />
              </div>
            ) : (
              <>
                {showFilter && (
                  <FilterFlexioPool
                    setShowFilter={setShowFilter}
                    onFilterChange={handleFilterChange}
                    defaultValues={filterValues}
                    handleClickFilter={handleClickFilter}
                    onReset={handleReset}
                  />
                )}
                <div className="pl-3">
                  <Checkbox onChange={handleSelectAll}>Select All</Checkbox>
                </div>
                <Table
                  scroll={{ x: "100%" }}
                  bordered={false}
                  columns={columns}
                  dataSource={poolPackages}
                  rowKey="id"
                  pagination={pagination}
                  footer={false}
                  rowSelection={rowSelection}
                />
              </>
            )}
          </>
        )}
        {selectedInfo.typePack === "dropoff" && (
          <SelectPack
            packages={getPacksFromSelect()}
            packageTypeList={packageTypeList}
            formSelectPack={formSelectPack}
            dropId="dropoff"
            dropoffAddress={
              form.getFieldValue(LOCATION_TYPES.DROPOFF)?.address || "Dropoff"
            }
            pickups={[
              {
                id: pickupLocationId || params.eid || "pickup",
                location_name:
                  form.getFieldValue(LOCATION_TYPES.PICKUP)?.location_name || "Pickup",
                address: { address: form.getFieldValue(LOCATION_TYPES.PICKUP)?.address }
              }
            ]}
            dropoffs={[
              {
                id: "dropoff",
                location_name:
                  form.getFieldValue(LOCATION_TYPES.DROPOFF)?.location_name || "Dropoff",
                address: { address: form.getFieldValue(LOCATION_TYPES.DROPOFF)?.address }
              }
            ]}
            onOk={handleSelectPack}
          />
        )}
      </Modal>
    </div>
  );
};

export default withPermission(AddJobPage, [UserRole.ADMIN, UserRole.MANAGER]);
